<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <h1 class="pb-15">
        <v-svg class="voxo-green" sprite="clock" />
        Global Time of Days
      </h1>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
        </div>
        <div class="box right">
          <el-button @click="openCreateModal">Add New</el-button>
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{enabled: true, gt: 50, oSize: 0}"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <create-form
      :createVisible="createVisible"
      :createProcessing="createProcessing"
      :clearFormFields="clearFormFields"
      :timezones="timezones"
      :customTypeOptions="customTypeOptions"
      :globalConditionOptions="globalConditionOptions"
      :conditionOptions="conditionOptions"
      @createOperation="createOperation"
      @cancelOperation="cancelOperation"
    />

    <edit-form
      :editVisible="editVisible"
      :editProcessing="editProcessing"
      :clearFormFields="clearFormFields"
      :timezones="timezones"
      :customTypeOptions="customTypeOptions"
      :globalConditionOptions="globalConditionOptions"
      :conditionOptions="conditionOptions"
      :selectedTimeOfDay="selectedTimeOfDay"
      @editOperation="editOperation"
      @cancelOperation="cancelOperation"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import feathersClient from "../../../feathers/feathers-client";
import CreateForm from "./components/createForm";
import EditForm from "./components/editForm";
import {debounce} from "lodash";
import {globalEventBus} from "@/globalEventBus";

export default {
  name: "GlobalTimeOfDaysTable",
  data() {
    return {
      conditionsChannel: null,
      createProcessing: false,
      editProcessing: false,
      deleteProcessing: false,
      tableLoading: false,
      tableData: [],
      allConditions: [],
      search: "",
      editVisible: false,
      createVisible: false,
      clearFormFields: false,
      visibleActionsId: "",
      selectedTimeOfDay: null,
      timezones: [],
      globalConditionOptions: [],
      customTypeOptions: [
        {
          label: "All listed conditions satisfied",
          value: "ALL"
        },
        {
          label: "Any listed conditions satisfied",
          value: "ANY"
        },
        {
          label: "Calendar",
          value: "CALENDAR"
        },
        {
          label: "Weektime",
          value: "WEEKTIME"
        }
      ],
      tableColumns: [
        {
          field: 'sprite',
          title: '',
          width: 50,
          slots: {
            default: ({row}) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-green" sprite="clock"/>
                </div>
              ]
            }
          }
        },
        {
          field: 'name',
          title: 'Name',
          minWidth: 150,
          sortable: true
        },
        {
          field: 'type',
          title: 'Type',
          minWidth: 150,
          sortable: true
        },
        {
          field: 'actions',
          title: '',
          minWidth: '150',
          showOverflow: false,
          slots: {
            default: ({row}) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={'edit-'+ row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={'delete-'+row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteOperation(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>
              ]
            }
          }
        }
      ]
    };
  },

  components: {
    CreateForm,
    EditForm
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    user() {
      return this.$store.state.auth.user;
    },

    conditionOptions() {
      return [...this.tableData, ...this.globalConditionOptions];
    }
  },

  watch: {
    selectedTenant() {
      this.findConditions();
      feathersClient.service('channel-memberships').remove(null, {query: {channelName: this.conditionsChannel}});
      this.joinSocketChannel();
    },
    user() {
      this.getTimezoneOptions();
      this.findConditions();
      if(!this.conditionsChannel){
        this.joinSocketChannel();
      }
    },
    search: debounce(function () {
      this.searchConditions();
    }, 500),
  },

  methods: {
    ...mapActions("conditions", {
      deleteConditions: "remove",
      createConditions: "create",
      patchConditions: "patch"
    }),

    searchConditions() {
      if(this.search.length){
        this.tableData = this.allConditions.filter(
          data =>
            data.name.toLowerCase().includes(this.search.toLowerCase()) ||
            data.type.toLowerCase().includes(this.search.toLowerCase())
        )
      } else{
        this.tableData = [...this.allConditions];
      }
    },

    //General Form and Table Operations
    cellClicked(data){
      if(data.column.property !== 'actions'){
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data){
      this.visibleActionsId = data.row.id;
    },

    rowLeave(){
      this.visibleActionsId = "";
    },

    openCreateModal() {
      this.createVisible = true;
      this.clearFormFields = false;
    },

    openEditModal(id) {
      this.editVisible = true;
      this.clearFormFields = false;
      feathersClient
        .service("conditions")
        .get(id)
        .then(res => {
          this.selectedTimeOfDay = res;
          this.editVisible = true;
        });
    },

    createOperation(params) {
      const createBody = { ...params };

      this.$confirm("Process time of day create?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.createProcessing = true;
          this.createConditions(createBody)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Created successfully!",
                type: "success"
              });
              this.createProcessing = false;
              this.clearFormFields = true;
              this.cancelOperation("create");
            })
            .catch(error => {
              this.clearFormFields = false;

              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }

              this.createProcessing = false;
            });
        })
        .catch(() => true);
    },

    editOperation(editBody) {
      this.$confirm("Process time of day edit?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.editProcessing = true;

          this.patchConditions([this.selectedTimeOfDay.id, editBody])
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Edited successfully!",
                type: "success"
              });
              this.editProcessing = false;
              this.clearFormFields = true;
              this.cancelOperation("edit");
            })
            .catch(error => {
              this.clearFormFields = false;

              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }

              this.editProcessing = false;
            });
        })
        .catch(() => true);
    },

    cancelOperation(type) {
      this.clearFormFields = true;
      this[`${type}Visible`] = false;
    },

    deleteOperation(id) {
      this.$confirm("Process time of day delete?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.deleteProcessing = true;

          this.deleteConditions(id)
            .then(() => {
              this.deleteProcessing = false;
              this.$notify({
                title: "Success",
                message: "Deleted successfully!",
                type: "success"
              });
            })
            .catch(error => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }
              this.deleteProcessing = false;
            });
        })
        .catch(() => true);
    },

    findConditions() {
      this.tableLoading = true;
      feathersClient.service('conditions').find({
        query: {
          $sort: {name: 1},
          tenantId: 0
        }
      }).then(res => {
        this.tableData = [...res];
        this.allConditions = [...res];
        this.tableLoading = false;
      }).catch( () => {
        this.tableLoading = false;
      });
    },

    getTimezoneOptions() {
      feathersClient.service('timezones').find({
        query: {
          $sort: {
            description: 1
          }
        }
      }).then(res => {
        this.timezones = res;
      });
    },

    joinSocketChannel(){
      const channelService = feathersClient.service('channel-memberships');
      const data = {
        roomType: 'global-admins'
      };
      channelService.create(data).then(res => {this.conditionsChannel = `${data.roomType}`});
    },

    removeListeners(){
      feathersClient.service('conditions').removeListener('patched');
      feathersClient.service('conditions').removeListener('created');
      feathersClient.service('conditions').removeListener('removed');
      globalEventBus.$off('appReconnected');
    }
  },
  created() {
    //event listeners
    //monitor socket connection and reconnect to channel
    globalEventBus.$on('appReconnected', () => {
      this.joinSocketChannel();
    });

    //record was edited
    //always refresh did destination options
    feathersClient.service('conditions').on('patched', (data) => {
      const updateIndex = this.tableData.findIndex(item => item.id === data.id);
      const updateAllIndex = this.allConditions.findIndex(item => item.id === data.id);
      if(updateIndex >= 0){this.tableData.splice(updateIndex, 1, data);}
      if(updateAllIndex >= 0){ this.allConditions.splice(updateAllIndex, 1, data);}
    });

    //record was created
    feathersClient.service('conditions').on('created', (data) => {
      this.tableData.push(data);
      this.allConditions.push(data);
    });

    //record was deleted
    feathersClient.service('conditions').on('removed', (data) => {
      const indexToRemove = this.tableData.findIndex(item => item.id === data.id);
      const indexAllToRemove = this.allConditions.findIndex(item => item.id === data.id);
      if(indexToRemove >= 0){this.tableData.splice(indexToRemove, 1);}
      if(indexAllToRemove >= 0){this.allConditions.splice(indexAllToRemove, 1);}
    });
  },
  mounted() {
    if (this.user) {
      this.joinSocketChannel();
      this.getTimezoneOptions();
      this.findConditions();
    }

  },
  beforeDestroy() {
    feathersClient.service('channel-memberships').remove(null ,{query: {channelName: this.conditionsChannel}});
    this.removeListeners();
  }
};
</script>
